import { memo, useCallback, useEffect, useRef, useState } from "react";

/**
 * Matrix-style scramble-then-reveal text. Vendored from the onboarding
 * app's `decrypted-text.tsx` so the intake modal can use the same fancy
 * title treatment without depending on the onboarding-app package.
 */

const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: "1px",
  height: "1px",
  padding: 0,
  margin: "-1px",
  overflow: "hidden",
  clip: "rect(0,0,0,0)",
  border: 0,
};

interface DecryptedTextProps {
  text: string;
  speed?: number;
  maxIterations?: number;
  sequential?: boolean;
  revealDirection?: "start" | "end" | "center";
  characters?: string;
  className?: string;
  parentClassName?: string;
  encryptedClassName?: string;
  animateOn?: "hover" | "view" | "both" | "mount";
}

export const DecryptedText = memo(function DecryptedText({
  text,
  speed = 50,
  maxIterations = 10,
  sequential = false,
  revealDirection = "start",
  characters = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz!@#$%^&*()",
  className = "",
  parentClassName = "",
  encryptedClassName = "",
  animateOn = "view",
}: DecryptedTextProps) {
  const [displayText, setDisplayText] = useState(text);
  const [isHovering, setIsHovering] = useState(false);
  const [isScrambling, setIsScrambling] = useState(false);
  const [revealedIndices, setRevealedIndices] = useState<Set<number>>(
    new Set(),
  );
  const [hasAnimated, setHasAnimated] = useState(false);
  const containerRef = useRef<HTMLSpanElement>(null);

  const startAnimation = useCallback(() => {
    setDisplayText(
      text
        .split("")
        .map((char) => (char === " " ? " " : (characters[0] ?? char)))
        .join(""),
    );
    setRevealedIndices(new Set());
    setIsHovering(true);
    setHasAnimated(true);
  }, [characters, text]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    let currentIteration = 0;

    const getNextIndex = (revealedSet: Set<number>) => {
      const textLength = text.length;
      switch (revealDirection) {
        case "start":
          return revealedSet.size;
        case "end":
          return textLength - 1 - revealedSet.size;
        case "center": {
          const middle = Math.floor(textLength / 2);
          const offset = Math.floor(revealedSet.size / 2);
          const nextIndex =
            revealedSet.size % 2 === 0 ? middle + offset : middle - offset - 1;
          if (
            nextIndex >= 0 &&
            nextIndex < textLength &&
            !revealedSet.has(nextIndex)
          ) {
            return nextIndex;
          }
          for (let i = 0; i < textLength; i++) {
            if (!revealedSet.has(i)) {
              return i;
            }
          }
          return 0;
        }
        default:
          return revealedSet.size;
      }
    };

    const availableChars = characters.split("");

    const shuffleText = (
      originalText: string,
      currentRevealed: Set<number>,
    ) => {
      return originalText
        .split("")
        .map((char, i) => {
          if (char === " ") {
            return " ";
          }
          if (currentRevealed.has(i)) {
            return originalText[i];
          }
          return availableChars[
            Math.floor(Math.random() * availableChars.length)
          ];
        })
        .join("");
    };

    if (isHovering) {
      setIsScrambling(true);
      interval = setInterval(() => {
        setRevealedIndices((prevRevealed) => {
          if (sequential) {
            if (prevRevealed.size < text.length) {
              const nextIndex = getNextIndex(prevRevealed);
              const newRevealed = new Set(prevRevealed);
              newRevealed.add(nextIndex);
              setDisplayText(shuffleText(text, newRevealed));
              return newRevealed;
            } else {
              clearInterval(interval);
              setIsScrambling(false);
              return prevRevealed;
            }
          } else {
            setDisplayText(shuffleText(text, prevRevealed));
            currentIteration++;
            if (currentIteration >= maxIterations) {
              clearInterval(interval);
              setIsScrambling(false);
              setDisplayText(text);
            }
            return prevRevealed;
          }
        });
      }, speed);
    } else {
      setDisplayText(text);
      setRevealedIndices(new Set());
      setIsScrambling(false);
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [
    isHovering,
    text,
    speed,
    maxIterations,
    sequential,
    revealDirection,
    characters,
  ]);

  useEffect(() => {
    if (animateOn !== "mount") {
      return;
    }

    const timeout = window.setTimeout(startAnimation, 120);
    return () => window.clearTimeout(timeout);
  }, [animateOn, startAnimation]);

  useEffect(() => {
    if (animateOn !== "view" && animateOn !== "both") {
      return;
    }

    const triggerAnimation = () => {
      if (!hasAnimated) {
        startAnimation();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            triggerAnimation();
          }
        }
      },
      { threshold: 0.1 },
    );

    const el = containerRef.current;
    let animationFrame: number | undefined;
    if (el) {
      observer.observe(el);
      animationFrame = requestAnimationFrame(() => {
        const rect = el.getBoundingClientRect();
        const isInViewport =
          rect.bottom >= 0 &&
          rect.right >= 0 &&
          rect.top <= window.innerHeight &&
          rect.left <= window.innerWidth;
        if (isInViewport) {
          triggerAnimation();
        }
      });
    }

    return () => {
      if (animationFrame != null) {
        cancelAnimationFrame(animationFrame);
      }
      if (el) {
        observer.unobserve(el);
      }
    };
  }, [animateOn, hasAnimated, startAnimation]);

  const hoverProps =
    animateOn === "hover" || animateOn === "both"
      ? {
          onMouseEnter: () => setIsHovering(true),
          onMouseLeave: () => setIsHovering(false),
        }
      : {};

  return (
    <span
      className={parentClassName}
      ref={containerRef}
      style={{ display: "inline-block" }}
      {...hoverProps}
    >
      <span style={SR_ONLY}>{displayText}</span>
      <span aria-hidden="true">
        {displayText.split("").map((char, index) => {
          const isRevealedOrDone =
            revealedIndices.has(index) || !isScrambling || !isHovering;
          return (
            <span
              className={isRevealedOrDone ? className : encryptedClassName}
              key={index}
            >
              {char}
            </span>
          );
        })}
      </span>
    </span>
  );
});
